import React, { useState, useEffect } from "react";
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Shield,
  UserCheck,
  UserX,
  Save,
  X,
  Clock,
  CheckCircle,
  Mail,
  Phone,
  Briefcase,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  membroService,
  Membro,
  MembroComMandato,
  TipoMembro,
  RepresentacaoMembro,
} from "../services/membroService";
import { mandatoService, MandatoComNomes } from "../services/mandatoService";
import { notifyError, notifySuccess } from "../utils/notify";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export const GestaoMembros: React.FC = () => {
  const { user, empresaSelecionada } = useAuth();
  const [membros, setMembros] = useState<MembroComMandato[]>([]);
  const [mandatos, setMandatos] = useState<MandatoComNomes[]>([]);
  const [loading, setLoading] = useState(true);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [membroEditando, setMembroEditando] = useState<MembroComMandato | null>(
    null,
  );
  const [mostrarDialogExcluir, setMostrarDialogExcluir] = useState(false);
  const [membroParaExcluir, setMembroParaExcluir] = useState<string | null>(
    null,
  );
  const [filtroMandato, setFiltroMandato] = useState<string>("todos");
  const [filtroStatus, setFiltroStatus] = useState<string>("todos");

  const [novoMembro, setNovoMembro] = useState<Partial<Membro>>({
    nome: "",
    email: "",
    cargo: "",
    tipo: "eleito",
    representacao: "empregados",
    matricula: "",
    setor: "",
    telefone: "",
    data_posse: new Date().toISOString().split("T")[0],
    reeleito: false,
    numero_reeleicoes: 0,
    ativo: true,
    observacoes: "",
  });

  const carregarDados = async () => {
    if (!empresaSelecionada?.id) return;

    try {
      setLoading(true);
      const [membrosData, mandatosData] = await Promise.all([
        membroService.listar(empresaSelecionada.id),
        mandatoService.listar(empresaSelecionada.id),
      ]);
      setMembros(membrosData);
      setMandatos(mandatosData);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      notifyError("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empresaSelecionada?.id) {
      carregarDados();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaSelecionada]);

  const abrirModalNovo = () => {
    const mandatoAtivo = mandatos.find((m) => m.status === "ativo");

    setNovoMembro({
      nome: "",
      email: "",
      cargo: "",
      tipo: "eleito",
      representacao: "empregados",
      matricula: "",
      setor: "",
      telefone: "",
      data_posse: new Date().toISOString().split("T")[0],
      reeleito: false,
      numero_reeleicoes: 0,
      ativo: true,
      observacoes: "",
      mandato_id: mandatoAtivo?.id || undefined,
    });
    setMembroEditando(null);
    setMostrarModal(true);
  };

  const abrirModalEditar = (membro: MembroComMandato) => {
    setNovoMembro({
      nome: membro.nome,
      email: membro.email || "",
      cargo: membro.cargo,
      tipo: membro.tipo,
      representacao: membro.representacao,
      matricula: membro.matricula || "",
      setor: membro.setor || "",
      telefone: membro.telefone || "",
      data_posse: membro.data_posse,
      data_saida: membro.data_saida || "",
      reeleito: membro.reeleito,
      numero_reeleicoes: membro.numero_reeleicoes,
      ativo: membro.ativo,
      observacoes: membro.observacoes || "",
      mandato_id: membro.mandato_id,
    });
    setMembroEditando(membro);
    setMostrarModal(true);
  };

  const salvarMembro = async () => {
    if (!empresaSelecionada?.id || !user?.id) {
      notifyError("Empresa ou usuário não identificado");
      return;
    }

    if (!novoMembro.nome || !novoMembro.cargo || !novoMembro.mandato_id) {
      notifyError("Nome, cargo e mandato são obrigatórios");
      return;
    }

    try {
      if (membroEditando?.id) {
        // Atualizar
        await membroService.atualizar(membroEditando.id, novoMembro);
        notifySuccess("Membro atualizado com sucesso!");
      } else {
        // Criar novo
        const membroCompleto: Omit<Membro, "id" | "created_at" | "updated_at"> =
          {
            empresa_id: empresaSelecionada.id,
            usuario_id: null,
            mandato_id: novoMembro.mandato_id!,
            nome: novoMembro.nome!,
            email: novoMembro.email || null,
            cpf_hash: null,
            cargo: novoMembro.cargo!,
            tipo: (novoMembro.tipo as TipoMembro) || "eleito",
            representacao:
              (novoMembro.representacao as RepresentacaoMembro) || "empregados",
            matricula: novoMembro.matricula || null,
            setor: novoMembro.setor || null,
            telefone: novoMembro.telefone || null,
            data_posse: novoMembro.data_posse!,
            data_saida: novoMembro.data_saida || null,
            estabilidade_ate: undefined,
            reeleito: novoMembro.reeleito || false,
            numero_reeleicoes: novoMembro.numero_reeleicoes || 0,
            ativo: novoMembro.ativo !== false,
            observacoes: novoMembro.observacoes || null,
            criado_por: user.id,
          };

        await membroService.criar(membroCompleto);
        notifySuccess("Membro criado com sucesso!");
      }

      setMostrarModal(false);
      carregarDados();
    } catch (error) {
      console.error("Erro ao salvar membro:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Erro ao salvar membro";
      notifyError(errorMessage);
    }
  };

  const confirmarExclusao = (id: string) => {
    setMembroParaExcluir(id);
    setMostrarDialogExcluir(true);
  };

  const excluirMembro = async () => {
    if (!membroParaExcluir) return;

    try {
      await membroService.excluir(membroParaExcluir);
      notifySuccess("Membro excluído com sucesso!");
      setMostrarDialogExcluir(false);
      setMembroParaExcluir(null);
      carregarDados();
    } catch (error) {
      console.error("Erro ao excluir membro:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Erro ao excluir membro";
      notifyError(errorMessage);
    }
  };

  const toggleAtivo = async (id: string, ativo: boolean) => {
    try {
      if (ativo) {
        await membroService.reativar(id);
        notifySuccess("Membro reativado com sucesso!");
      } else {
        await membroService.desativar(id, "Desativado manualmente");
        notifySuccess("Membro desativado com sucesso!");
      }
      carregarDados();
    } catch (error) {
      console.error("Erro ao alterar status:", error);
      notifyError("Erro ao alterar status do membro");
    }
  };

  const membrosFiltrados = membros.filter((m) => {
    if (filtroMandato !== "todos" && m.mandato_id !== filtroMandato)
      return false;
    if (filtroStatus === "ativos" && !m.ativo) return false;
    if (filtroStatus === "inativos" && m.ativo) return false;
    return true;
  });

  const estatisticas = {
    total: membros.length,
    ativos: membros.filter((m) => m.ativo).length,
    eleitos: membros.filter((m) => m.tipo === "eleito").length,
    indicados: membros.filter((m) => m.tipo === "indicado").length,
    comEstabilidade: membros.filter((m) => membroService.temEstabilidade(m))
      .length,
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <Clock className="w-8 h-8 text-blue-600 animate-pulse mx-auto mb-2" />
          <p className="text-gray-600">Carregando membros...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              Gestão de Membros da CIPA
            </h1>
            <p className="text-purple-100">{empresaSelecionada?.nome}</p>
            <p className="text-sm text-purple-200 mt-1">
              Conforme NR-05 - Eleitos e Indicados
            </p>
          </div>
          <button
            onClick={abrirModalNovo}
            className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Novo Membro
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-purple-500">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-gray-700">
              Total de Membros
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {estatisticas.total}
          </p>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-green-500">
          <div className="flex items-center gap-2 mb-2">
            <UserCheck className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-gray-700">Ativos</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {estatisticas.ativos}
          </p>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-blue-500">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">Eleitos</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {estatisticas.eleitos}
          </p>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-yellow-500">
          <div className="flex items-center gap-2 mb-2">
            <UserCheck className="w-4 h-4 text-yellow-600" />
            <span className="text-sm font-medium text-gray-700">Indicados</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {estatisticas.indicados}
          </p>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-lg border-l-4 border-red-500">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-red-600" />
            <span className="text-sm font-medium text-gray-700">
              C/ Estabilidade
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {estatisticas.comEstabilidade}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-lg p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filtrar por Mandato
            </label>
            <select
              value={filtroMandato}
              onChange={(e) => setFiltroMandato(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg"
            >
              <option value="todos">Todos os Mandatos</option>
              {mandatos.map((m) => (
                <option key={m.id} value={m.id}>
                  Mandato #{m.numero_mandato} ({m.status})
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filtrar por Status
            </label>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg"
            >
              <option value="todos">Todos</option>
              <option value="ativos">Somente Ativos</option>
              <option value="inativos">Somente Inativos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Members List */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-600" />
          Membros Cadastrados ({membrosFiltrados.length})
        </h3>

        {membrosFiltrados.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-2">Nenhum membro encontrado</p>
            {membros.length > 0 ? (
              <p className="text-sm text-gray-400">
                Ajuste os filtros para ver mais resultados
              </p>
            ) : (
              <button
                onClick={abrirModalNovo}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Cadastrar primeiro membro
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {membrosFiltrados.map((membro) => {
              const temEstabilidade = membroService.temEstabilidade(membro);
              const diasRestantes =
                membroService.diasEstabilidadeRestantes(membro);

              return (
                <div
                  key={membro.id}
                  className={`border rounded-lg p-4 ${
                    !membro.ativo ? "bg-gray-50 opacity-75" : ""
                  } ${temEstabilidade ? "border-l-4 border-l-red-500" : ""}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-lg font-semibold text-gray-900">
                          {membro.nome}
                        </h4>
                        {!membro.ativo && (
                          <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full">
                            Inativo
                          </span>
                        )}
                        {temEstabilidade && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full flex items-center gap-1">
                            <Shield className="w-3 h-3" />
                            Estabilidade
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{membro.cargo}</p>
                    </div>

                    <div className="flex gap-1">
                      <button
                        onClick={() => abrirModalEditar(membro)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => toggleAtivo(membro.id!, !membro.ativo)}
                        className={`p-2 rounded ${
                          membro.ativo
                            ? "text-yellow-600 hover:bg-yellow-50"
                            : "text-green-600 hover:bg-green-50"
                        }`}
                        title={membro.ativo ? "Desativar" : "Reativar"}
                      >
                        {membro.ativo ? (
                          <UserX className="w-4 h-4" />
                        ) : (
                          <UserCheck className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => confirmarExclusao(membro.id!)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                    <div className="flex items-center gap-1 text-gray-600">
                      <Briefcase className="w-3 h-3" />
                      <span className="font-medium">Tipo:</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs ${
                          membro.tipo === "eleito"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-purple-100 text-purple-800"
                        }`}
                      >
                        {membro.tipo === "eleito" ? "Eleito" : "Indicado"}
                      </span>
                    </div>
                    <div className="text-gray-600">
                      <span className="font-medium">Representação:</span>{" "}
                      {membro.representacao === "empregados"
                        ? "Empregados"
                        : "Empregador"}
                    </div>
                  </div>

                  {(membro.email || membro.telefone) && (
                    <div className="space-y-1 text-sm text-gray-600 mb-3">
                      {membro.email && (
                        <div className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {membro.email}
                        </div>
                      )}
                      {membro.telefone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {membro.telefone}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="text-xs text-gray-500 border-t pt-2">
                    <div className="flex justify-between">
                      <span>Mandato #{membro.mandato_numero || "N/A"}</span>
                      <span>
                        Posse:{" "}
                        {new Date(membro.data_posse).toLocaleDateString(
                          "pt-BR",
                        )}
                      </span>
                    </div>
                    {temEstabilidade && (
                      <div className="mt-1 text-red-600 font-medium">
                        🛡️ Estabilidade por mais {diasRestantes} dias
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal Criar/Editar */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {membroEditando ? "Editar Membro" : "Novo Membro"}
                </h3>
                <button
                  onClick={() => setMostrarModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Mandato */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mandato *
                  </label>
                  <select
                    value={novoMembro.mandato_id || ""}
                    onChange={(e) =>
                      setNovoMembro({
                        ...novoMembro,
                        mandato_id: e.target.value,
                      })
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">Selecione um mandato</option>
                    {mandatos.map((m) => (
                      <option key={m.id} value={m.id}>
                        Mandato #{m.numero_mandato} - {m.status} (
                        {new Date(m.data_inicio).toLocaleDateString("pt-BR")} a{" "}
                        {new Date(m.data_fim).toLocaleDateString("pt-BR")})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Dados Pessoais */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      value={novoMembro.nome || ""}
                      onChange={(e) =>
                        setNovoMembro({ ...novoMembro, nome: e.target.value })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cargo *
                    </label>
                    <input
                      type="text"
                      value={novoMembro.cargo || ""}
                      onChange={(e) =>
                        setNovoMembro({ ...novoMembro, cargo: e.target.value })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                </div>

                {/* Tipo e Representação */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo *
                    </label>
                    <select
                      value={novoMembro.tipo || "eleito"}
                      onChange={(e) =>
                        setNovoMembro({
                          ...novoMembro,
                          tipo: e.target.value as TipoMembro,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    >
                      <option value="eleito">Eleito</option>
                      <option value="indicado">Indicado</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Representação *
                    </label>
                    <select
                      value={novoMembro.representacao || "empregados"}
                      onChange={(e) =>
                        setNovoMembro({
                          ...novoMembro,
                          representacao: e.target.value as RepresentacaoMembro,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    >
                      <option value="empregados">Empregados</option>
                      <option value="empregador">Empregador</option>
                    </select>
                  </div>
                </div>

                {/* Contato */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={novoMembro.email || ""}
                      onChange={(e) =>
                        setNovoMembro({ ...novoMembro, email: e.target.value })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <input
                      type="tel"
                      value={novoMembro.telefone || ""}
                      onChange={(e) =>
                        setNovoMembro({
                          ...novoMembro,
                          telefone: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Dados Funcionais */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Matrícula
                    </label>
                    <input
                      type="text"
                      value={novoMembro.matricula || ""}
                      onChange={(e) =>
                        setNovoMembro({
                          ...novoMembro,
                          matricula: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Setor
                    </label>
                    <input
                      type="text"
                      value={novoMembro.setor || ""}
                      onChange={(e) =>
                        setNovoMembro({ ...novoMembro, setor: e.target.value })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Datas */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Posse *
                    </label>
                    <input
                      type="date"
                      value={novoMembro.data_posse || ""}
                      onChange={(e) =>
                        setNovoMembro({
                          ...novoMembro,
                          data_posse: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                  {!novoMembro.ativo && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Data de Saída
                      </label>
                      <input
                        type="date"
                        value={novoMembro.data_saida || ""}
                        onChange={(e) =>
                          setNovoMembro({
                            ...novoMembro,
                            data_saida: e.target.value,
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      />
                    </div>
                  )}
                </div>

                {/* Reeleição */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="reeleito"
                      checked={novoMembro.reeleito || false}
                      onChange={(e) =>
                        setNovoMembro({
                          ...novoMembro,
                          reeleito: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-blue-600"
                    />
                    <label
                      htmlFor="reeleito"
                      className="text-sm font-medium text-gray-700"
                    >
                      Membro reeleito
                    </label>
                  </div>
                  {novoMembro.reeleito && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Número de Reeleições
                      </label>
                      <input
                        type="number"
                        value={novoMembro.numero_reeleicoes || 0}
                        onChange={(e) =>
                          setNovoMembro({
                            ...novoMembro,
                            numero_reeleicoes: parseInt(e.target.value),
                          })
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        min="0"
                        max="1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        NR-05: Máximo de 1 reeleição
                      </p>
                    </div>
                  )}
                </div>

                {/* Status Ativo */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="ativo"
                    checked={novoMembro.ativo !== false}
                    onChange={(e) =>
                      setNovoMembro({ ...novoMembro, ativo: e.target.checked })
                    }
                    className="w-4 h-4 text-blue-600"
                  />
                  <label
                    htmlFor="ativo"
                    className="text-sm font-medium text-gray-700"
                  >
                    Membro ativo
                  </label>
                </div>

                {/* Observações */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    value={novoMembro.observacoes || ""}
                    onChange={(e) =>
                      setNovoMembro({
                        ...novoMembro,
                        observacoes: e.target.value,
                      })
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    rows={3}
                    placeholder="Observações sobre o membro..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setMostrarModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={salvarMembro}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {membroEditando ? "Atualizar" : "Cadastrar Membro"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog
        open={mostrarDialogExcluir}
        onOpenChange={setMostrarDialogExcluir}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este membro? Esta ação não pode ser
              desfeita.
              {membros.find((m) => m.id === membroParaExcluir)?.ativo && (
                <span className="block mt-2 text-yellow-600 font-medium">
                  ⚠️ Atenção: Este membro está ativo!
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={excluirMembro}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
